'use client'

import { forwardRef, useState } from 'react'
import { regular } from '@fortawesome/fontawesome-svg-core/import.macro'

import { labelStyles, textStyles } from './styles'
import { mtc, TypographyStyles } from '@/styles'
import { Button } from '../button'
import { Label } from '../label'
import { Icon } from '../icon'
import { List } from '../list'
import { Text } from '../text'

type FileInputRenderFunction = (props: {
  files: File[]
  onRemove: (index: number) => void
}) => React.ReactNode

export interface IFileInput
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'children'> {
  className?: string
  disabled?: boolean
  error?: boolean
  errorTestId?: string
  label?: string
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  placeholder?: string
  children?: React.ReactNode | FileInputRenderFunction
}

/** An input for uploading files */
export const FileInput = forwardRef<HTMLInputElement, IFileInput>(
  (
    {
      className,
      disabled = false,
      error,
      errorTestId,
      label = 'Choose File',
      onChange,
      placeholder,
      children,
      ...props
    },
    ref
  ) => {
    const [documentsToUpload, setDocumentsToUpload] = useState<File[]>([])

    const handleDocumentsToUpload = (
      event: React.ChangeEvent<HTMLInputElement>
    ) => {
      const docs = event.target.files

      if (docs) {
        setDocumentsToUpload((prev) => [...prev, ...Array.from(docs)])
      }

      onChange(event)
    }

    const handleDelete = (fileIndex: number) => {
      const updatedFiles = documentsToUpload.filter(
        (_, index) => index !== fileIndex
      )
      setDocumentsToUpload(updatedFiles)

      if (updatedFiles.length === 0 && ref && 'current' in ref && ref.current) {
        ref.current.value = ''
      }
    }

    return (
      <div className={`relative inline-flex ${className}`}>
        <input
          id="doc-upload"
          ref={ref}
          type="file"
          className="opacity-0 w-0 h-0"
          placeholder={placeholder}
          disabled={disabled}
          data-testid={errorTestId}
          onChange={handleDocumentsToUpload}
          multiple
          {...props}
        />

        <Label
          htmlFor="doc-upload"
          disabled={disabled}
          className={mtc(labelStyles({ disabled, error }))}
        >
          <Button disabled={disabled} variant="secondary" asChild>
            <span>{label}</span>
          </Button>

          {documentsToUpload.length === 0 ? (
            <Text>No files chosen</Text>
          ) : children ? (
            typeof children === 'function' ? (
              children({ files: documentsToUpload, onRemove: handleDelete })
            ) : (
              children
            )
          ) : (
            <>
              <Text variant={TypographyStyles.EYEBROW}>Files to upload</Text>

              <List className="pl-0">
                {documentsToUpload.map((doc: File, index: number) => (
                  <List.Item
                    key={`${doc.name}-${doc.lastModified}`}
                    className="flex flex-row"
                  >
                    <Text
                      className={mtc(
                        textStyles({
                          error,
                          isFileUploaded: documentsToUpload.length > 0,
                        })
                      )}
                    >
                      {doc.name}
                    </Text>

                    <Icon
                      icon={regular('trash-can')}
                      onClick={() => handleDelete(index)}
                      className="ml-2 cursor-pointer"
                    />
                  </List.Item>
                ))}
              </List>
            </>
          )}
        </Label>
      </div>
    )
  }
)

FileInput.displayName = 'FileInput'
